/**
 * CRM sync — the outbound port for pushing a LEAD to the CRM.
 *
 * A lead is somebody sales has yet to qualify, whatever brought them in. ÖPM
 * produces four kinds (ARCH-040 §Lead types); LandVest adds analyses. All land on ONE board,
 * separated by the `Lead-Typ` column and by views — never by object, and never
 * by board. A Lead IS the unqualified person record; the Kontakt is created at
 * qualification, not before.
 *
 * Each application owns its pure projections into `CrmLead` — no IO, no
 * Supabase — which is
 * what makes them exhaustively testable and what keeps the "which column gets
 * which value" decision out of the push service.
 *
 * WHY WE CARRY A "known contact" FLAG. Monday's own duplicate detection does not
 * help here, verified against the live account:
 *   - the stock "Indications" column compares Leads↔Leads and Leads↔Accounts,
 *     NEVER the Kontakte board — and this account's Leads board does not even
 *     have that column;
 *   - "Manage duplicates" is manual, per-board, and merges by ARCHIVING the
 *     loser;
 *   - "Move to Contacts" is literally a board move that matches nothing.
 * We can do better than all three, because every ÖPM-imported contact carries
 * `Kontakt-ID (ÖPM)` = our `users.id`: an EXACT uuid match rather than text
 * equality on an email or a freetext company name.
 */

/**
 * The `Lead-Typ` labels ÖPM writes.
 *
 * The ÖPM labels were read from `color_mm6nsbn0` on 2026-08-28. Provision
 * `LandVest-Analyse` on that column before enabling the LandVest adapter.
 * Monday rejects the ENTIRE mutation for a
 * label the board does not carry, so a typo here does not mis-tag a lead, it
 * loses the whole card. Note the `ü` in `Rückruf`.
 *
 * The board carries a fifth label, `Outbound`, which sales applies by hand to
 * target-list rows. ÖPM never writes it: nothing we observe is outbound.
 */
import type { INBOUND_LEAD_TYPES } from "./lead-admission-policy.mjs";
export type LeadType = (typeof INBOUND_LEAD_TYPES)[number];

/** The application artefact a lead was projected from. */
export type LeadSourceKind = "request" | "offering" | "callback" | "contact" | "analysis" | "email";

export interface LeadSource {
  kind: LeadSourceKind;
  /** Primary key of the row on that source's table. */
  id: string;
}

/** A CRM-neutral projection of one inbound lead. */
/** An identity field a lead projection can take from the user's profile. */
export type ProfileIdentityField = "name" | "company" | "phone";

export interface CrmLead {
  /** Origin label for this lead, overriding the application default. */
  sourceSystem?: string;
  /** Stable source URI for find-before-create and recovery after partial delivery. */
  sourceKey?: string;
  /** Which application artefact this is, and its id — the `entity_links` subject. */
  source: LeadSource;
  leadType: LeadType;
  /**
   * `user:<uuid>` | `mail:<addr>` | `phone:<digits>` — the person-level dedupe
   * key (see `./identity`).
   *
   * Non-nullable ON PURPOSE: a lead nobody can be reached at is not a lead, so
   * the projections return `null` instead of a keyless `CrmLead`.
   *
   * READ BACK by `findOpenLeadsForPerson`, which searches EVERY form this person
   * could be filed under (`personKeyCandidates`), so a new artefact can be merged
   * onto their open card. It is stamped only on CREATE: rewriting it on a merge
   * replaced the key the card was found by, so the next artefact under the older
   * form no longer matched and minted a duplicate.
   */
  personKey: string;
  /**
   * When the artefact arrived, as an ISO timestamp, or null when the row has
   * none. The SLA clock starts here, so it comes off the ROW and never from
   * `Date.now()` — a backfill or a retry must not restart somebody's deadline.
   *
   * Nullable rather than `""`: an empty string is a sentinel every consumer has
   * to know about, and `new Date("")` is `Invalid Date`.
   */
  receivedAt: string | null;
  /** The person's display name — the CRM item's name. */
  name: string;
  /**
   * True when `name` is something WE invented because the person gave us none —
   * a fallback to their email or phone, or a literal like "Unbekannter
   * Interessent".
   *
   * Set by whoever invents it, because only the projection can tell a fallback
   * from a real answer. The adapter previously guessed by matching against a
   * list of four literals, which missed the two commonest cases: a callback
   * named after its raw phone number, and `formatUserName` falling through to
   * the email address. A rep who renamed such a card had it overwritten back on
   * the next republish — the exact clobber the guess existed to prevent.
   */
  nameIsPlaceholder: boolean;
  /**
   * The identity fields this projection took from the user's PROFILE, as opposed to the
   * source's own answers (a callback's typed name and number). Set by the projection, the
   * only place that knows. The identity fill records exactly these as the customer's, so a
   * later profile change follows them while the source's answers stay. Absent: none.
   */
  profileFields?: ProfileIdentityField[];
  email: string | null;
  phone: string | null;
  company: string | null;
  bundesland: string | null;
  plz: string | null;
  ort: string | null;
  naturraumLabel: string | null;
  /** Volume, in the product's own unit (Punkte, or Hektar for Flächen). */
  quantity: number | null;
  /**
   * Ausgleichsfläche area in m², when that is the product.
   *
   * Derived from the hectare-denominated quantity — neither `requests` nor
   * `offerings` stores an m² column.
   */
  areaSqm: number | null;
  /**
   * The BUYER's stated ceiling. Null for a seller lead: an Angebot carries an
   * ASK, which is the opposite number, and writing it into „Preis Käufer" would
   * read as a buyer's budget. A seller's price goes in `note` until the board
   * grows a column of its own.
   */
  maxPrice: number | null;
  /** Delivery window as German prose ("15.03.2027" / "3 Monate"), never a raw enum. */
  deliveryBy: string | null;
  /** Raw product token — the unit vocabulary hangs off it. */
  productType: string | null;
  /**
   * The product in German, or null when the artefact has none — a Rückruf is a
   * request to be phoned, not a thing to buy. Nullable so no future source has
   * to invent a product word just to satisfy the type.
   */
  productLabel: string | null;
  /**
   * What the person actually said, in their words — the chat intent, the contact
   * form's topic and message, the seller's ask. Rendered into the item body.
   */
  note: string | null;
  /**
   * Deep link back into application admin, or null when the artefact has no admin page.
   *
   * Callbacks and contact submissions have none — they are reachable only
   * through the MCP tools — so the body omits the link rather than shipping a
   * URL that 404s.
   */
  adminUrl: string | null;
  /**
   * The Monday item id of an EXISTING contact for this person, or null.
   *
   * Resolved by the push service via `Kontakt-ID (ÖPM)`. Present means "we
   * already know this person" — sales should read history rather than treat the
   * lead as cold.
   */
  knownContactId: string | null;
  /**
   * The compensation axes the lead asks about (ARCH-039), one entry per axis and
   * species or element: `compensation_axes.key`, the subject (its register name,
   * or as written when it is not in the register) and the `axis_units.unit_key`.
   * Optional: only sources that know them set it.
   */
  axes?: CrmLeadAxis[];
}

export interface CrmLeadAxis {
  axisKey: string;
  subjectLabel: string | null;
  unitKey: string | null;
}

export interface CrmLeadPort {
  /**
   * Create this artefact's card, or refresh the one it already has.
   *
   * `existingItemId` means "THIS artefact's card" — established by
   * `entity_links`, so a full column refresh is right: the row moved and the
   * card should follow.
   */
  pushLead(lead: CrmLead, existingItemId: string | null): Promise<CrmLeadSyncResult>;

  /**
   * Record a DIFFERENT artefact from the same person on an existing card.
   *
   * A separate operation from `pushLead`, and the distinction is the whole
   * point: this card belongs to another artefact. Its Bundesland, PLZ, volume,
   * price, `Lead-Typ` and SLA clock describe THAT one, and a merge that
   * refreshed columns would interleave the two into a card describing neither —
   * "Bayern / 14467 / 200 Punkte", a request nobody filed.
   *
   * So this writes NO columns at all. It posts what the person now wants as an
   * update, which is additive, timestamped and impossible to corrupt.
   */
  annotateLead(lead: CrmLead, itemId: string): Promise<CrmLeadSyncResult>;
}

export interface CrmLeadSyncResult {
  ok: boolean;
  externalItemId?: string;
  /** Canonical entity pointer, built by the adapter (only it knows its board). */
  externalItemUrl?: string;
  error?: string;
}

/**
 * The author row shared by the two user-backed projections (Anfrage, Angebot).
 *
 * `is_anonymous` is here because the dedupe key depends on it and nothing else
 * can supply it — see `./identity`.
 */
export interface LeadUserSource {
  id: string;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  company_name?: string | null;
  phone?: string | null;
  /**
   * Postgres' own E.164 for this person (`users.phone_e164`, GENERATED).
   *
   * Carried alongside the raw column so the key ladder normalises the number the
   * same way the login path does — see `canonicalPhone` in `./identity`.
   */
  phone_e164?: string | null;
  is_anonymous?: boolean | null;
}

/** The default adapter: does nothing, successfully. */
export const NoopCrmLeadAdapter: CrmLeadPort = {
  async pushLead() {
    return { ok: true };
  },
  async annotateLead() {
    return { ok: true };
  },
};
